import React, { useState, useEffect } from "react";
import "./modal.css";
import "./BulkUpdateAIModal.css";
import { toast, ToastContainer } from "react-toastify";
import { getUsersForBulkUpdate, bulkUpdateAI } from "../../services/userService";
import { RotatingLines } from "react-loader-spinner";

const BulkUpdateAIModal = ({ setIsOpen, setLoadingUsers }) => {
  const [loading, setLoading] = useState(false);
  const [fetchingUsers, setFetchingUsers] = useState(true);
  const [allUsers, setAllUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [selectedAI, setSelectedAI] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  // Fetch all users on component mount
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setFetchingUsers(true);
        const res = await getUsersForBulkUpdate();
        if (res?.status === "success") {
          setAllUsers(res.data || []);
          setFilteredUsers(res.data || []);
        } else {
          toast("Failed to fetch users");
        }
      } catch (error) {
        toast(error?.response?.data?.message || "An error occurred while fetching users");
      } finally {
        setFetchingUsers(false);
      }
    };

    fetchUsers();
  }, []);

  // Filter users based on search query
  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredUsers(allUsers);
    } else {
      const filtered = allUsers.filter(
        (user) =>
          user.Name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          user.email?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredUsers(filtered);
    }
  }, [searchQuery, allUsers]);

  // Handle individual checkbox toggle
  const handleCheckboxToggle = (userId) => {
    setSelectedUserIds((prev) => {
      if (prev.includes(userId)) {
        return prev.filter((id) => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  // Handle select all / deselect all
  const handleSelectAll = () => {
    if (selectedUserIds.length === filteredUsers.length && filteredUsers.length > 0) {
      setSelectedUserIds([]);
    } else {
      const allFilteredIds = filteredUsers.map((user) => user.id);
      setSelectedUserIds(allFilteredIds);
    }
  };

  // Handle bulk update
  const handleBulkUpdate = async () => {
    if (selectedUserIds.length === 0) {
      toast("Please select at least one user");
      return;
    }

    if (!selectedAI) {
      toast("Please select an AI engine");
      return;
    }

    try {
      setLoading(true);
      const res = await bulkUpdateAI({ userIds: selectedUserIds, aiEngine: selectedAI });

      if (res?.status === "success") {
        toast(res.message || "AI engine updated successfully");
        setLoadingUsers(true);
        setTimeout(() => {
          setIsOpen(false);
        }, 1000);
      } else {
        throw new Error("Update failed");
      }
    } catch (error) {
      toast(
        error?.response?.data?.message ||
          "An error occurred. Please try again"
      );
    } finally {
      setLoading(false);
    }
  };

  const getAIDisplayName = (ai) => {
    switch (ai) {
      case "BASTIONGPT":
        return "BASTION-GPT";
      case "OPENAI":
        return "OPEN-AI";
      case "GPT5":
        return "GPT-5";
      default:
        return ai;
    }
  };

  const isAllSelected = selectedUserIds.length === filteredUsers.length && filteredUsers.length > 0;

  return (
    <>
      <div className="darkBG" onClick={() => setIsOpen(false)} />
      <ToastContainer />
      <div className="centered">
        <div className="modal bulk-update-modal" style={{ maxWidth: "520px", width: "90%", position: "relative" }}>
          <div className="modalHeader" style={{ position: "relative" }}>
            <h5 className="heading" style={{ fontSize: "17px", fontWeight: "600" }}>Bulk Update AI Engine</h5>
            <button className="closeBtn" onClick={() => setIsOpen(false)}>
              &times;
            </button>
          </div>

          <div style={{ padding: "18px" }}>

            {/* Search Box */}
            <div style={{ marginBottom: "12px" }}>
              <input
                type="text"
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  border: "1px solid #DEE2E6",
                  borderRadius: "5px",
                  outline: "none",
                  fontSize: "13px",
                  fontFamily: "inherit",
                }}
              />
            </div>

            {/* Users List */}
            {fetchingUsers ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  padding: "30px",
                }}
              >
                <RotatingLines
                  strokeColor="#6E75FF"
                  strokeWidth="5"
                  animationDuration="0.75"
                  width="40"
                  visible={true}
                />
              </div>
            ) : (
              <>
                {/* Select All Checkbox */}
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "10px",
                  backgroundColor: "#F8F9FA",
                  borderRadius: "5px",
                  marginBottom: "10px",
                  cursor: "pointer",
                }}
                onClick={handleSelectAll}
                >
                  <div className={`custom-checkbox ${isAllSelected ? 'checked' : ''}`} />
                  <span style={{
                    fontSize: "13px",
                    fontWeight: "500",
                    color: "#495057",
                    marginLeft: "10px",
                  }}>
                    Select All ({filteredUsers.length})
                  </span>
                </div>

                {/* Scrollable Users List */}
                <div
                  className="bulk-update-users-list"
                  style={{
                    maxHeight: "260px",
                    overflowY: "auto",
                    border: "1px solid #DEE2E6",
                    borderRadius: "5px",
                    marginBottom: "12px",
                  }}
                >
                  {filteredUsers.length === 0 ? (
                    <div
                      style={{
                        padding: "30px",
                        textAlign: "center",
                        color: "#6C757D",
                        fontSize: "13px",
                      }}
                    >
                      No users found
                    </div>
                  ) : (
                    filteredUsers.map((user, index) => (
                      <div
                        key={user.id}
                        className={`bulk-update-user-row ${selectedUserIds.includes(user.id) ? 'selected' : ''}`}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          padding: "10px",
                          borderBottom: index < filteredUsers.length - 1 ? "1px solid #F0F0F0" : "none",
                          cursor: "pointer",
                        }}
                        onClick={() => handleCheckboxToggle(user.id)}
                      >
                        <div className={`custom-checkbox ${selectedUserIds.includes(user.id) ? 'checked' : ''}`} />
                        <div style={{ flex: 1, minWidth: 0, marginLeft: "10px" }}>
                          <div style={{
                            fontSize: "13px",
                            fontWeight: "500",
                            color: "#212529",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}>
                            {user.Name}
                          </div>
                          <div style={{
                            fontSize: "11px",
                            color: "#6C757D",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            marginTop: "2px",
                          }}>
                            {user.email}
                          </div>
                        </div>
                        <div
                          style={{
                            fontSize: "10px",
                            padding: "4px 8px",
                            borderRadius: "4px",
                            backgroundColor: "#E7F0FF",
                            color: "#6E75FF",
                            fontWeight: "600",
                            flexShrink: 0,
                            marginLeft: "10px",
                          }}
                        >
                          {getAIDisplayName(user.AI)}
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* Selected Count */}
                <div
                  style={{
                    fontSize: "12px",
                    color: "#495057",
                    marginBottom: "12px",
                  }}
                >
                  <strong style={{ color: "#6E75FF" }}>{selectedUserIds.length}</strong> user(s) selected
                </div>
              </>
            )}

            {/* AI Engine Dropdown */}
            <div style={{ marginBottom: "18px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "6px",
                  fontSize: "13px",
                  fontWeight: "500",
                  color: "#495057",
                }}
              >
                Select New AI Engine
              </label>
              <select
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  outline: "none",
                  border: "1px solid #DEE2E6",
                  color: selectedAI ? "#212529" : "#ADB5BD",
                  borderRadius: "5px",
                  fontSize: "13px",
                  backgroundColor: "white",
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
                onChange={(e) => setSelectedAI(e.target.value)}
                value={selectedAI}
              >
                <option value="" disabled>
                  Choose AI Engine
                </option>
                <option value="BASTIONGPT">BASTION-GPT</option>
                <option value="OPENAI">OPEN-AI</option>
                <option value="DEFAULT">DEFAULT</option>
                <option value="GPT5">GPT-5</option>
              </select>
            </div>

            {/* Action Buttons */}
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "8px",
              }}
            >
              <button
                onClick={() => setIsOpen(false)}
                style={{
                  padding: "9px 18px",
                  border: "1px solid #DEE2E6",
                  borderRadius: "5px",
                  backgroundColor: "white",
                  color: "#495057",
                  cursor: "pointer",
                  fontSize: "13px",
                  fontWeight: "500",
                  fontFamily: "inherit",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleBulkUpdate}
                disabled={loading}
                style={{
                  padding: "9px 18px",
                  border: "none",
                  borderRadius: "5px",
                  backgroundColor: loading ? "#ADB5BD" : "#6E75FF",
                  color: "white",
                  cursor: loading ? "not-allowed" : "pointer",
                  fontSize: "13px",
                  fontWeight: "500",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  fontFamily: "inherit",
                }}
              >
                {loading && (
                  <RotatingLines
                    strokeColor="white"
                    strokeWidth="5"
                    animationDuration="0.75"
                    width="16"
                    visible={true}
                  />
                )}
                {loading ? "Updating..." : "Update Users"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BulkUpdateAIModal;
